import { useEffect, useRef } from "react";

// `scroll-snap-type: mandatory` fights trackpad/mouse-wheel momentum in
// Chrome/Safari — mid-gesture, the browser keeps trying to pull the page back
// to the current section, which reads as stutter or the page refusing to
// move at all on anything but a big, decisive flick. This takes wheel input
// over entirely instead: one real wheel gesture moves exactly one section via
// a single smooth scrollIntoView, so there's nothing left to fight. CSS keeps
// `scroll-snap-type: y proximity` as a cheap, non-fighting fallback for
// keyboard and touch scrolling — this hook is only wired to `wheel`.

// Gap (ms) between consecutive wheel events below which the next event is
// treated as the same physical gesture (its burst or decaying inertia tail)
// rather than a new flick. Real silence between two separate gestures
// reliably exceeds this; trackpad momentum doesn't.
const QUIET_MS = 160;
// Backstop only — the primary unlock path is the `scrollend` event. Kept in
// case `scrollend` doesn't fire (unsupported browser, interrupted scroll).
const FALLBACK_UNLOCK_MS = 700;

export default function useSectionScrollSnap(selector = ".section") {
  const lockedRef = useRef(false);
  const lastWheelAtRef = useRef(0);
  const fallbackTimerRef = useRef(null);

  useEffect(() => {
    const getSections = () => Array.from(document.querySelectorAll(selector));

    const closestIndex = (sections) => {
      let closest = 0;
      let closestDist = Infinity;
      sections.forEach((el, i) => {
        const dist = Math.abs(el.getBoundingClientRect().top);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      return closest;
    };

    const unlock = () => {
      lockedRef.current = false;
      clearTimeout(fallbackTimerRef.current);
      window.removeEventListener("scrollend", unlock);
    };

    const handleWheel = (e) => {
      // ignore trackpad noise — a real gesture clears this easily, and
      // letting the rest fall through to native scroll keeps small residual
      // momentum from feeling locked out entirely
      if (Math.abs(e.deltaY) < 4) return;

      e.preventDefault();

      // Always measure against the true last tick, even ones we're about to
      // swallow below — otherwise a late inertia tick landing right as the
      // lock clears reads as a brand-new gesture and fires a jump after the
      // user has already stopped scrolling.
      const now = performance.now();
      const gap = now - lastWheelAtRef.current;
      lastWheelAtRef.current = now;

      if (lockedRef.current || gap < QUIET_MS) return;

      const sections = getSections();
      if (!sections.length) return;

      const index = closestIndex(sections);
      const nextIndex = index + (e.deltaY > 0 ? 1 : -1);
      const target = sections[Math.max(0, Math.min(sections.length - 1, nextIndex))];
      if (!target) return;

      lockedRef.current = true;
      target.scrollIntoView({ behavior: "smooth", block: "start" });

      // scrollend is the real "did it finish" signal; the timeout is only a
      // fallback for when it doesn't fire.
      window.addEventListener("scrollend", unlock, { once: true });
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = setTimeout(unlock, FALLBACK_UNLOCK_MS);
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("scrollend", unlock);
      clearTimeout(fallbackTimerRef.current);
    };
  }, [selector]);
}
